<template>
  <HgCardElevated
    v-bind="props"
    :class="[
      $style.container,
      {
        [$style.is24]: isIs24,
        [$style.hg]: !isIs24,
      },
    ]"
  >
    <div :class="$style.contentWrapper" @click="isCardUnLinked && $event.stopPropagation()">
      <div
        v-if="slots.title"
        :class="[
          $style.header,
          {
            [$style.spaceBetween]: iconPosition === 'right',
          },
        ]"
      >
        <h3
          :class="[
            $style.heading,
            {
              [$style.is24]: isIs24,
            },
          ]"
        >
          <slot name="title" />
        </h3>
        <HgIcon
          v-if="hasInfoIcon"
          icon="InfoCircle"
          color="gray"
          :class="$style.infoIcon"
          data-testid="info-icon"
          @click="handleModalOpen"
        />
      </div>
      <p v-if="slots.description" :class="$style.description" data-testid="description">
        <slot name="description" />
      </p>

      <slot />
    </div>
  </HgCardElevated>
</template>

<script lang="ts" setup>
import { HgCardElevated, HgIcon } from '@smg-real-estate/domus';
import { computed, type Slots, useSlots } from 'vue';

import { isPlatformIs24 } from '@/helpers/platform';

type ClassType = string | object | string[] | object[];
interface Props {
  hasInfoIcon?: boolean;
  iconPosition?: 'static' | 'right';
  href?: string;
  to?: string;
  shouldOpenInNewWindow?: boolean;
  actionsCssClass?: ClassType;
  ctaCssClass?: ClassType;
}
const props = withDefaults(defineProps<Props>(), {
  hasInfoIcon: false,
  iconPosition: 'right',
  href: undefined,
  to: undefined,
  shouldOpenInNewWindow: false,
  actionsCssClass: undefined,
  ctaCssClass: undefined,
});

const isIs24 = isPlatformIs24();

const slots: Slots = useSlots();
const emit = defineEmits<{
  // TODO find the lint rule to match the Composition API
  /* eslint-disable unused-imports/no-unused-vars */
  (e: 'onInfoIconClick'): void;
}>();
const isCardUnLinked = computed(() => !props.to && !props.href);

const handleModalOpen = (event: Event): void => {
  emit('onInfoIconClick');
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

@mixin card-shadow {
  > div[class*='HgCardElevated_content'],
  > a[class*='HgCardElevated_content'] {
    box-shadow: $elevation-minimal;

    &:hover,
    &:focus {
      box-shadow: $elevation-minimal;
    }
  }
}

.container {
  @include card-shadow;
}

.contentWrapper {
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;

  &.spaceBetween {
    align-items: baseline;
    justify-content: space-between;
  }
}

.heading {
  margin-top: 0;
  margin-right: $space-50;
  margin-bottom: 0;

  @include heading-3;
}

.description {
  margin-top: 0;
  margin-bottom: 0;
  color: $color-grey-100;

  @include body-3;
}

.infoIcon {
  cursor: pointer;
}

.closeButton {
  margin: $space-100;
}

.container.hg {
  > div[class*='HgCardElevated_content'],
  > a[class*='HgCardElevated_content'] {
    box-shadow: none;
  }

  @include grid-media-query(sm) {
    @include card-shadow;
  }

  .contentWrapper {
    @include grid-media-query(sm) {
      padding: $space-150;
    }
  }
}

.container.is24 {
  .heading {
    @include heading-3-regular;
  }

  .contentWrapper {
    padding: $space-150;
  }
}
</style>
